import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Employee, EmployeeStatus, EmployeeType } from '@ffd/api-client';
import { ConfirmDialogComponent, ConfirmDialogResult, InputComponent, TooltipModule } from '@libs/ui-lib';
import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TagEvent, TagPusherService } from '@libs/shared-services';
import { routes } from '@libs/utils';
import { RouterLink } from '@angular/router';
import { LinkedPatientDialogResult } from '../link-patient/link-patient.interface';
import { SelectedParticipantComponent, SelectedParticipant } from '../selected-participant';
import { ParticipantListItem, SearchParticipantsComponent } from '../search-participants';
import { COLLAB_TITLE_MAX_LENGTH, CollaborationDynamicTitlePipe } from '../../pipes/collaboration-dynamic-title';
import {
  CollaborationEditorDialogData,
  CollaborationEditorDialogResult,
  CollaborationEditorForm,
} from './collaboration-editor-dialog.interface';
import { CollaborationEditorDialogService } from './collaboration-editor-dialog.service';

const MINIMUM_PARTICIPANTS = 1;

@Component({
  selector: 'lib-collaboration-editor-dialog',
  imports: [
    CommonModule,
    InputComponent,
    TranslateModule,
    ReactiveFormsModule,
    SearchParticipantsComponent,
    SvgIconComponent,
    SelectedParticipantComponent,
    CollaborationDynamicTitlePipe,
    RouterLink,
    TooltipModule,
  ],
  providers: [CollaborationEditorDialogService],
  templateUrl: './collaboration-editor-dialog.component.html',
  styleUrl: './collaboration-editor-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollaborationEditorDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchProviders') public searchProvidersInputComponent!: SearchParticipantsComponent;
  @ViewChild('searchPatients') public searchPatientsInputComponent!: SearchParticipantsComponent;

  public dialogData: CollaborationEditorDialogData = inject(DIALOG_DATA);
  public dialog = inject(Dialog);
  private readonly dialogRef = inject(DialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly screenService = inject(CollaborationEditorDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly tagPusherService = inject(TagPusherService);

  protected currentUser = this.screenService.currentUser;
  protected employeeParticipants = computed(() => this.screenService.viewModel().employeeParticipants);
  protected emailParticipants = computed(() => this.screenService.viewModel().emailParticipants ?? []);
  protected patientParticipant = computed(() => this.screenService.viewModel().patientParticipant);
  protected allParticipants = computed(() => this.screenService.viewModel().allParticipants);
  protected linkedPatient = computed(() => this.screenService.viewModel().linkedPatient);
  protected employees = computed(() => this.screenService.viewModel().employeeOptions);
  protected patients = computed(() => this.screenService.viewModel().patientOptions);
  protected employeesLoading = computed(() => this.screenService.viewModel().employeesLoading);
  protected siteId = computed(() => this.screenService.viewModel().siteId);
  protected isShowcase = computed(() => this.siteId() === routes.showcaseSiteId);

  protected subjectMaxLength = COLLAB_TITLE_MAX_LENGTH;

  protected invitationRequested = signal<boolean>(false);
  protected noParticipantError = signal<boolean>(false);

  protected selectedSiteName: string | undefined;

  public routes = routes;
  private readonly linkedPatientDialogRef: DialogRef<LinkedPatientDialogResult> | null = null;
  private readonly linkedPatientRemovalDialogRef: DialogRef<ConfirmDialogResult> | null = null;
  private providerInvitationDialogRef: DialogRef<ConfirmDialogResult> | null = null;
  private readonly patientInvitationDialogRef: DialogRef<ConfirmDialogResult> | null = null;

  public readonly form = new FormGroup<CollaborationEditorForm>({
    siteId: new FormControl(null, [Validators.required]),
    subject: new FormControl('', { nonNullable: true }),
  });

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public ngOnInit(): void {
    this.screenService.init();
    if (this.dialogData.siteId) {
      this.screenService.setSiteId(this.dialogData.siteId);
      this.form.controls.siteId.setValue(this.dialogData.siteId);

      if (this.dialogData.isEditing) {
        this.selectedSiteName = this.dialogData.sites.find((site) => site.value === this.dialogData.siteId)?.label;
      }

      this.screenService.searchProviders({ searchTerm: undefined });
    }

    const totalParticipantCount =
      this.dialogData.employeeParticipants?.length ??
      0 + (this.dialogData.patientParticipant ? 1 : 0) + (this.dialogData.emailParticipants?.length ?? 0);

    const removable = (this.dialogData.isCreatedBySelf || this.dialogData.isUserSiteAdmin) && totalParticipantCount > 1;

    if (this.dialogData.employeeParticipants) {
      this.screenService.setParticipantsFromPersons(
        this.dialogData.employeeParticipants.map((participant) => ({
          ...participant,
          irremovable: !removable || participant.userId === this.dialogData.userId,
        })),
      );
    }
    if (this.dialogData.patientParticipant) {
      this.screenService.setPatientParticipant({
        ...this.dialogData.patientParticipant,
        irremovable: !removable || this.dialogData.patientParticipant.userId === this.dialogData.userId,
      });
    }

    if (this.dialogData.emailParticipants) {
      this.screenService.setEmailParticipant(
        this.dialogData.emailParticipants.map((participant) => ({
          ...participant,
          irremovable: !removable || participant.userId === this.dialogData.userId,
        })),
      );
    }
    if (this.dialogData.linkedPatient) {
      this.screenService.setLinkedPatient(this.dialogData.linkedPatient);
    }

    if (this.dialogData.subject) {
      this.form.controls.subject.setValue(this.dialogData.subject);
    }

    this.listenToFormChanges();
  }

  public ngAfterViewInit(): void {
    // the input is not in the dom yet, so we need to wait for the next tick
    setTimeout(() => {
      this.searchProvidersInputComponent?.focusSearchInput();
    }, 0);
  }

  public ngOnDestroy(): void {
    this.linkedPatientDialogRef?.close();
    this.linkedPatientRemovalDialogRef?.close();
    this.providerInvitationDialogRef?.close();
    this.patientInvitationDialogRef?.close();
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public onClearSubjectClick(): void {
    this.form.controls.subject.setValue('');
  }

  public saveCollab(): void {
    const minimumParticipants = this.dialogData.isEditing ? MINIMUM_PARTICIPANTS + 1 : MINIMUM_PARTICIPANTS;

    if (
      this.employeeParticipants().length + Number(this.patientParticipant() !== null) + (this.emailParticipants()?.length ?? 0) <
      minimumParticipants
    ) {
      this.noParticipantError.set(true);
      return;
    }

    this.dialogRef.close({
      siteId: this.siteId() ?? null,
      subject: this.form.controls.subject.value,
      employees: this.employeeParticipants(),
      patient: this.patientParticipant(),
      emailParticipants: this.emailParticipants() ?? [],
      linkedPatient: this.linkedPatient(),
    } satisfies CollaborationEditorDialogResult);

    if (this.invitationRequested()) {
      this.tagPusherService.pushTag({ event: TagEvent.CollaborationInvitationRequested });
    }
  }

  protected onSearchProviders(searchTerm: string | null): void {
    if (!this.isShowcase()) {
      this.screenService.updateProviderSearchTerm(searchTerm);
    }
  }

  protected onProvidersEndReached(): void {
    this.screenService.searchProviders({ searchTerm: undefined, endReached: true });
  }

  protected onAddProviderParticipant(participant: ParticipantListItem): void {
    const participantEmployee = participant as Employee;

    if (this.isShowcase()) {
      this.screenService.appendEmailParticipant({
        id: participantEmployee.id,
        email: participantEmployee.pmsEmail,
        participantRole: EmployeeType.PROVIDER,
        status: participantEmployee.status,
        firstName: participantEmployee.firstName,
        lastName: participantEmployee.lastName,
      });
      this.tagPusherService.pushTag({ event: TagEvent.CollaborationInvitationSent });
    } else {
      const appendProvider = (): void => {
        this.screenService.appendEmployeeParticipant({
          id: participantEmployee.id,
          userId: participantEmployee.userId,
          firstName: participantEmployee.firstName,
          lastName: participantEmployee.lastName,
          pmsEmployeeId: participantEmployee.pmsId,
          email: participantEmployee.pmsEmail,
          status: participantEmployee.status,
          participantRole: participantEmployee.employeeType,
        });

        this.noParticipantError.set(false);
      };

      if (participantEmployee.status === EmployeeStatus.NOT_INVITED) {
        const description = this.translateService.instant('collab_new_collab_modal_inviteParticipantAlert_message', {
          input1: `${participantEmployee.firstName} ${participantEmployee.lastName}`,
          input2: `${participantEmployee.lastName}`,
        });

        this.providerInvitationDialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
          backdropClass: ['bg-overlay-light'],
          panelClass: ['w-[390px]'],
          disableClose: true,
          data: {
            title: 'collab_new_collab_modal_inviteParticipantAlert_title',
            description,
            cancelLabel: 'collab_new_collab_modal_inviteParticipantAlert_cancelButton_title',
            confirmLabel: 'collab_new_collab_modal_inviteParticipantAlert_inviteButton_title',
          },
        });

        this.providerInvitationDialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
          if (result?.confirm) {
            this.invitationRequested.set(true);
            appendProvider();
          }
        });

        return;
      }

      appendProvider();
    }
  }

  protected onRemoveEmployeeParticipant(participant: SelectedParticipant): void {
    this.screenService.removeEmployeeParticipant(participant);
  }

  protected onRemoveEmailParticipant(participant: SelectedParticipant): void {
    this.screenService.removeEmailParticipant(participant);
  }

  private listenToFormChanges(): void {
    this.form.controls.siteId.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value) {
        this.screenService.setSiteId(value);
      }
    });
  }
}
